import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import React, { ReactElement } from 'react';

import { useDialogState } from '../../../../../../../../../common/hooks/useDialogState';
import {
  IndicationStatus,
  InstitutionalIndicationOrderType,
  OrderBookState,
} from '../../../../../../../../../graphql';
import { useIndicationActivityBuySideContext } from '../../../../../context/IndicationActivityBuySideContext';
import { type Props as ReadButtonProps } from '../../../common/indication-section-read-view/ReadButtons';
import { PassIndicationDialog } from '../../../common/PassIndicationDialog';
import { usePassOnOffering } from '../../common/hooks/usePassOnOffering';
import CancelIndicationConfirmDialog from '../cancel-indication-confirm-dialog/CancelIndicationConfirmDialog';
import { useUpdateMyIndicationStatus } from './useUpdateMyIndicationStatus';

export type Params = Readonly<{
  onStartEditing: () => void;
  hasAllocationRanking: boolean;
}>;

const useCreateBuySideReadButtonProps = ({
  onStartEditing,
  hasAllocationRanking,
}: Params): ReadButtonProps & { readButtonDialogs: ReactElement[] } => {
  const shouldShowEdit = useCheckPermissions([permissionsByEntity.InstitutionalIndication.FULL]);

  const {
    basicPublishedOffering,
    offeringId,
    access: { canUpdateIndication },
  } = useIndicationActivityBuySideContext();

  const offering = basicPublishedOffering!;
  const indication = offering.myInstitutionalIndication!;

  const {
    isOpen: isPassDialogOpen,
    onOpen: openPassDialog,
    onClose: closePassDialog,
  } = useDialogState();
  const {
    isOpen: isCancelDialogOpen,
    onOpen: openCancelDialog,
    onClose: closeCancelDialog,
  } = useDialogState();

  const [onUpdateStatus, isUpdatingStatus] = useUpdateMyIndicationStatus(offeringId);
  const [onPass, isPassing] = usePassOnOffering({
    offeringId,
    indicationStatus: indication.status,
    onClose: closePassDialog,
  });
  const isDuplicate = indication.status === IndicationStatus.Duplicate;

  return {
    baseButtonAccess:
      offering.publicOrderBook?.state !== OrderBookState.Closed &&
      canUpdateIndication &&
      !isDuplicate,
    isVisible: {
      edit: !isDuplicate && indication.status !== IndicationStatus.Cancelled && shouldShowEdit,
      pass: !isDuplicate && indication.type !== InstitutionalIndicationOrderType.Pass,
      reactivate: indication.status === IndicationStatus.Cancelled,
      cancel: !isDuplicate && indication.status !== IndicationStatus.Cancelled,
    },
    editButtonProps: {
      disabled: isUpdatingStatus,
      onClick: onStartEditing,
    },
    menuItemProps: {
      pass: {
        disabled: isUpdatingStatus || hasAllocationRanking,
        onClick: openPassDialog,
      },
      reactivate: {
        disabled: isUpdatingStatus,
        onClick: () => onUpdateStatus(IndicationStatus.Active),
      },
      cancel: {
        disabled: isUpdatingStatus,
        onClick: openCancelDialog,
      },
    },
    readButtonDialogs: [
      <CancelIndicationConfirmDialog
        offeringId={offeringId}
        isOpen={isCancelDialogOpen}
        onClose={closeCancelDialog}
      />,
      <PassIndicationDialog
        isOpen={isPassDialogOpen}
        isLoading={isPassing}
        onClose={closePassDialog}
        onPass={onPass}
      />,
    ],
  };
};

export default useCreateBuySideReadButtonProps;
